import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, Article } from '../utils/api';
import { useLanguageStore } from '../stores/language';

interface RelatedArticlesProps {
  currentArticleId: string;
  tags: string[];
  limit?: number;
}

export function RelatedArticles({ currentArticleId, tags, limit = 3 }: RelatedArticlesProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguageStore();

  useEffect(() => {
    const fetchRelatedArticles = async () => {
      if (tags.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch articles for the first tag (most relevant)
        const primaryTag = tags[0];
        const { articles: tagArticles } = await api.getArticles({
          tag: primaryTag,
          limit: limit + 5 // Fetch extra to account for filtering out current article
        });

        // Filter out current article and limit results
        const related = tagArticles
          .filter(article => article.id !== currentArticleId)
          .slice(0, limit);

        // If we don't have enough, try other tags
        if (related.length < limit && tags.length > 1) {
          const existingIds = new Set([currentArticleId, ...related.map(a => a.id)]);

          for (let i = 1; i < tags.length && related.length < limit; i++) {
            const { articles: moreArticles } = await api.getArticles({
              tag: tags[i],
              limit: limit
            });

            for (const article of moreArticles) {
              if (!existingIds.has(article.id) && related.length < limit) {
                related.push(article);
                existingIds.add(article.id);
              }
            }
          }
        }

        setArticles(related);
      } catch (error) {
        console.error('Failed to fetch related articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedArticles();
  }, [currentArticleId, tags, limit]);

  if (loading) {
    return (
      <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          Related Articles
        </h2>
        <div className="flex justify-center py-4">
          <div className="spinner w-6 h-6" />
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return null;
  }

  return (
    <section className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        Related Articles
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {articles.map((article) => {
          const displayTitle = (language === 'zh' && article.title_zh)
            ? article.title_zh
            : article.title;

          return (
            <Link
              key={article.id}
              to={`/article/${article.slug}`}
              className="group block p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <h3 className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 line-clamp-2 mb-2">
                {displayTitle}
              </h3>
              {article.parsedTags && article.parsedTags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {article.parsedTags.slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}
